import { useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890!@#$%^&*()';

// ── DecryptedText ─────────────────────────────────────────────────────────────
// reactbits "decrypted-text": characters start scrambled and progressively
// resolve to the real text. Triggers on view (default) or hover.
export function DecryptedText({
  text = '',
  speed = 55,
  sequential = true,
  maxIterations = 14,
  revealDirection = 'start', // 'start' | 'end' | 'center'
  characters = DEFAULT_CHARS,
  animateOn = 'view', // 'view' | 'hover'
  className = '',
  style,
}) {
  const [output, setOutput] = useState(text);
  const ref = useRef(null);
  const running = useRef(false);

  // reveal order of indices for sequential mode
  const order = useMemo(() => {
    const n = text.length;
    const idx = Array.from({ length: n }, (_, i) => i);
    if (revealDirection === 'end') return idx.reverse();
    if (revealDirection === 'center') {
      const mid = (n - 1) / 2;
      return idx.sort((a, b) => Math.abs(a - mid) - Math.abs(b - mid));
    }
    return idx;
  }, [text, revealDirection]);

  const run = () => {
    if (running.current || !text) return;
    running.current = true;
    const rnd = () => characters[Math.floor(Math.random() * characters.length)];
    const len = text.length;

    if (sequential) {
      let step = 0;
      const timer = setInterval(() => {
        step += 1;
        const revealed = new Set(order.slice(0, step));
        const next = text
          .split('')
          .map((ch, i) => (ch === ' ' ? ' ' : revealed.has(i) ? ch : rnd()))
          .join('');
        setOutput(next);
        if (step >= len) { clearInterval(timer); setOutput(text); running.current = false; }
      }, speed);
      return;
    }

    let iter = 0;
    const timer = setInterval(() => {
      iter += 1;
      const t = iter / maxIterations;
      const next = text
        .split('')
        .map((ch) => (ch === ' ' ? ' ' : Math.random() < t ? ch : rnd()))
        .join('');
      setOutput(next);
      if (iter >= maxIterations) { clearInterval(timer); setOutput(text); running.current = false; }
    }, speed);
  };

  useEffect(() => {
    setOutput(text);
    running.current = false;
    if (animateOn !== 'view') return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { run(); io.disconnect(); } }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, animateOn]);

  const hoverProps = animateOn === 'hover' ? { onMouseEnter: run } : {};

  return (
    <span ref={ref} className={className} style={{ display: 'inline-block', whiteSpace: 'pre-wrap', ...style }} {...hoverProps}>
      {output}
    </span>
  );
}

// ── TextType ──────────────────────────────────────────────────────────────────
// reactbits "text-type": typewriter that types (and optionally deletes/cycles)
// one or more strings, with a blinking caret.
export function TextType({
  text = '',
  typingSpeed = 70,
  deletingSpeed = 40,
  pauseDuration = 1800,
  loop = true,
  showCursor = true,
  cursorChar = '|',
  className = '',
  style,
  as: Tag = 'span',
}) {
  const items = useMemo(() => (Array.isArray(text) ? text : [text]).filter((s) => s != null), [text]);
  const [display, setDisplay] = useState('');
  const [idx, setIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const current = items[idx % items.length] || '';
    let t;
    if (!deleting && display.length < current.length) {
      t = setTimeout(() => setDisplay(current.slice(0, display.length + 1)), typingSpeed);
    } else if (!deleting && display.length === current.length) {
      if (items.length === 1 && !loop) { setDone(true); return undefined; }
      t = setTimeout(() => setDeleting(true), pauseDuration);
    } else if (deleting && display.length > 0) {
      t = setTimeout(() => setDisplay(current.slice(0, display.length - 1)), deletingSpeed);
    } else if (deleting && display.length === 0) {
      setDeleting(false);
      setIdx((i) => i + 1);
    }
    return () => clearTimeout(t);
  }, [display, deleting, idx, items, typingSpeed, deletingSpeed, pauseDuration, loop]);

  return (
    <Tag className={className} style={{ ...style }}>
      {display}
      {showCursor && (
        <span className="fx-caret" style={{ opacity: done ? undefined : 1, marginLeft: 1 }} aria-hidden>
          {cursorChar}
        </span>
      )}
    </Tag>
  );
}
